import { useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'
import type { FormikErrors } from 'formik'

import type { LogVolunteeringActivityFormFields } from '../LogVolunteeringActivity.types'

import { useQuery } from '@percent/workplace-giving/common/hooks'
import { getAccountOpportunities } from '@percent/workplace-giving/api/opportunity/getAccountOpportunities/getAccountOpportunities'
import type {
  OpportunityDetailDto,
  OpportunitySelfParticipationDto
} from '@percent/workplace-giving/api/opportunity/dto'

const OPPORTUNITIES_PAGE_SIZE = 25

export type UseLogVolunteeringActivityFormProps = Readonly<{
  values: LogVolunteeringActivityFormFields
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<LogVolunteeringActivityFormFields>>
  setFieldTouched: (
    field: string,
    touched?: boolean | undefined,
    shouldValidate?: boolean | undefined
  ) => Promise<void | FormikErrors<LogVolunteeringActivityFormFields>>
  defaultOpportunity?: OpportunityDetailDto
}>

export type UseLogVolunteeringTimeResult = Readonly<{
  isFetchingRegisteredOpportunities: boolean
  isAwaitingFetchingRegisteredOpportunities: boolean
  fetchingRegisteredOpportunitiesError: boolean
  registeredOpportunitiesCount?: number
  registeredOpportunitiesQuery: string
  proposedRegisteredOpportunities?: OpportunitySelfParticipationDto[]
  registeredOpportunitiesData?: OpportunitySelfParticipationDto[]
  setRegisteredOpportunitiesQuery: (query: string) => void
  isLoading: boolean
  selectedOpportunityDetails?: OpportunitySelfParticipationDto['opportunity']
  setSelectedOpportunityDetails: (opportunity: OpportunitySelfParticipationDto['opportunity']) => void
}>

export const useLogVolunteeringActivityForm = ({
  defaultOpportunity
}: UseLogVolunteeringActivityFormProps): UseLogVolunteeringTimeResult => {
  const [selectedOpportunityDetails, setSelectedOpportunityDetails] = useState<
    undefined | OpportunitySelfParticipationDto['opportunity']
  >(defaultOpportunity as unknown as OpportunitySelfParticipationDto['opportunity'])

  const { data: fetchRegisteredOpportunitiesCountResponse, isPending: isLoadingRegisteredOpportunitiesCount } =
    useQuery(
      [
        'getAccountOpportunitiesCount',
        { pageSize: OPPORTUNITIES_PAGE_SIZE, participationStatus: ['registered', 'confirmed'] }
      ],
      getAccountOpportunities,
      { staleTime: Infinity }
    )

  const registeredOpportunitiesCount = fetchRegisteredOpportunitiesCountResponse?.totalResults

  const [registeredOpportunitiesQuery, setRegisteredOpportunitiesQuery] = useState<string>('')

  const [debouncedRegisteredOpportunitiesQuery] = useDebounceValue(registeredOpportunitiesQuery, 200)
  const {
    data: registeredOpportunitiesData,
    isFetching: isFetchingRegisteredOpportunities,
    isError: fetchingRegisteredOpportunitiesError,
    status
  } = useQuery(
    [
      'getAccountOpportunities',
      {
        query: debouncedRegisteredOpportunitiesQuery,
        pageSize: OPPORTUNITIES_PAGE_SIZE,
        participationStatus: ['registered', 'confirmed']
      }
    ],
    getAccountOpportunities,
    { enabled: !!registeredOpportunitiesCount && !!debouncedRegisteredOpportunitiesQuery }
  )

  return {
    registeredOpportunitiesCount,
    isAwaitingFetchingRegisteredOpportunities: status === 'pending',
    isFetchingRegisteredOpportunities,
    fetchingRegisteredOpportunitiesError,
    registeredOpportunitiesQuery,
    proposedRegisteredOpportunities: fetchRegisteredOpportunitiesCountResponse?.data,
    registeredOpportunitiesData: registeredOpportunitiesData?.data,
    setRegisteredOpportunitiesQuery,
    isLoading: isLoadingRegisteredOpportunitiesCount,
    selectedOpportunityDetails,
    setSelectedOpportunityDetails
  }
}
